export type Platform = 'Mobile' | 'Other' | 'PC' | 'Tablet';

export type PlatformAll = Platform | 'All';

export type WidgetVariantId =
  | 'DV_ckr3u0go30000dip199xf5kd5'
  | 'DV_cldn7q2lg00010tqfeqgu26bi';

export type DisplayVariant = {
  displayVariantId: WidgetVariantId;
};

export type AudioPlaybackMethod =
  | 'headlinerWithOriginal'
  | 'headlinerOnly'
  | 'originalOnly';

export interface Widget {
  widgetId: number;
  widgetSid: string;
  widgetName: string;
  selfServeRequestId: number;
  displayVariants: DisplayVariant[];
  audioPlaybackMethod?: AudioPlaybackMethod;
}

export interface PublisherDomain {
  domainId: number;
  domainSid: string;
  domain: string;
  displayName: string;
  widgets: Widget[];
  selfServeRequestId?: number;
}

export interface Publisher {
  domains: PublisherDomain[];
}

export interface GetPublisherWidgetArgs {
  isSelfServe?: boolean;
}

export type ReportingStatus =
  | 'setupInProgress'
  | 'live'
  | 'pausedClientRequest'
  | 'paused'
  | 'inactive'
  | 'completed'
  | 'archived';

export interface Campaign {
  id: number;
  campaignSid: string;
  name: string;
  reportingStatus?: ReportingStatus | null;
}

export interface Advertiser {
  id: number;
  name: string;
  campaigns: Campaign[];
}

export interface MyAdvertisers {
  advertisers: Advertiser[];
}

export interface ByDayWidgetMetric {
  dayMillis: number;
  widgetLoad: number;
  widgetInViewport: number;
  resultPlayed: number;
  resultClickThrough: number;
}

export interface ByDayWidgetResponse {
  data: ByDayWidgetMetric[];
}

export interface GetByDayWidgetMetricsArgs {
  startMillis: number;
  endMillis?: number;
  widgetIds?: string[];
}

export interface AdvertisersByDayWidgetMetric {
  dayMillis: number;
  spResultPlayed: number;
  spClickThrough: number;
  avgCpcMilliCent: number;
  spEngaged: number;
  totalCostMilliCent: number;
  unitLoad: number;
  unitInViewport: number;
}

export interface AdvertisersByDayWidgetResponse {
  data: AdvertisersByDayWidgetMetric[];
}

export type AdvertisersSourceType = 'domain' | 'app';

export type AdvertisersAppBundlePlatform = 'ios' | 'android';

export interface AdvertisersByDomainWidgetMetricAppInfo {
  bundleId: string;
  displayName: string;
  platform: AdvertisersAppBundlePlatform;
}

export interface AdvertisersByDomainWidgetMetricDomainInfo {
  domain: string;
}

export interface AdvertisersByDomainWidgetMetric {
  displayName: string;
  sourceType: AdvertisersSourceType;
  appInfo?: AdvertisersByDomainWidgetMetricAppInfo | null;
  domainInfo?: AdvertisersByDomainWidgetMetricDomainInfo | null;
  spResultPlayed: number;
  spClickThrough: number;
  avgCpcMilliCent: number;
  spEngaged: number;
  totalCostMilliCent: number;
  unitLoad: number;
  unitInViewport: number;
}

export interface AdvertisersByDomainWidgetResponse {
  data: AdvertisersByDomainWidgetMetric[];
}

export interface AdvertisersEpisodeLevelWidgetMetricEpisode {
  id: number;
  title: string;
}

export interface AdvertisersEpisodeLevelWidgetMetric {
  avgCpcMilliCent: number;
  episode: AdvertisersEpisodeLevelWidgetMetricEpisode;
  spClickThrough: number;
  spEngaged: number;
  spResultPlayed: number;
  totalCostMilliCent: number;
  unitInViewport: number;
  unitLoad: number;
}

export interface AdvertisersEpisodeLevelWidgetResponse {
  data: AdvertisersEpisodeLevelWidgetMetric[];
}

export interface GetAdvertisersByDayWidgetMetricsArgs {
  startMillis: number;
  endMillis?: number;
  campaignSids?: string[];
}

export interface GetAdvertisersByDomainWidgetMetricsArgs {
  startMillis: number;
  endMillis?: number;
  campaignSids?: string[];
}

export interface GetAdvertisersEpisodeLevelMetricsArgs {
  startMillis: number;
  endMillis?: number;
  campaignSids?: string[];
}

export interface GetAdvertisersBudgetInfoArgs {
  campaignSids?: string[];
}

export interface CampaignFeed {
  podcastFeedId: number;
  costBudgetMilliCent: number;
  costBudgetUsedMilliCent: number;
}

export interface CampaignBudget extends Campaign {
  campaignFeeds: CampaignFeed[];
}

export interface AdvertisersBudgetInfoResponse {
  campaigns: CampaignBudget;
  totalCostBudgetMilliCent: number;
  totalCostBudgetUsedMilliCent: number;
}

export interface GetPlatformByDayWidgetMetricsArgs {
  startMillis: number;
  endMillis?: number;
  widgetIds?: string[];
}

export interface GetPlatformByDayWidgetMetric {
  dayMillis: number;
  widgetLoad: number;
  widgetInViewport: number;
  resultPlayed: number;
  resultClickThrough: number;
  platform: Platform;
}

export interface GetPlatformByDayWidgetMetricsResponse {
  data: GetPlatformByDayWidgetMetric[];
}

export interface GetUrlWidgetMetricsArgs {
  startMillis: number;
  endMillis?: number;
  widgetIds?: string[];
}

export interface GetUrlWidgetMetric {
  resultClickThrough: number;
  resultPlayed: number;
  url: string;
  widgetInViewport: number;
  widgetLoad: number;
}

export interface GetUrlWidgetMetricsResponse {
  data: GetUrlWidgetMetric[];
}

export interface CreateWidgetArgs {
  rssUrl: string;
  websiteUrl: string;
  displayVariantId: string;
  audioPlaybackMethod: AudioPlaybackMethod;
}

export interface CreateWidgetResponse {
  creationStatus: WidgetCreationResponses;
}

export interface UpdateWidgetOptionsArgs {
  widgetId: string;
  displayVariantId?: string;
  audioPlaybackMethod?: AudioPlaybackMethod;
}
export interface UpdateWidgetStyleResponse {
  widgetId: string;
  isValid: boolean;
}
export type WidgetCreationResponses =
  | 'success'
  | 'unsupportedLanguage'
  | 'duplicateRssUrl'
  | 'widgetLimitReached';

export interface GetSelfServeWidgetArgs {
  selfServeRequestId: number;
}
export interface GetSelfServeWidgetsResponse {
  domains: SelfServePublisherDomain[];
}

export interface SelfServePublisherDomain
  extends Omit<PublisherDomain, 'widgets'> {
  widgets: SelfServeWidget[];
}

export interface SelfServeWidget extends Widget {
  widgetProcessingStatus: 'completed' | 'processing' | 'error';
}

export type SponsorPlacement =
  | 'headlinerApp'
  | 'publisherNetwork'
  | 'youtubePromotion';

export type SponsorContentType = 'podcastEpisode' | 'youtubeVideo';

export type CampaignCategories =
  | 'Arts & Entertainment'
  | 'Automotive'
  | 'Business'
  | 'Family & Parenting'
  | 'Food & Drink'
  | 'Health & Fitness'
  | 'Home & Garden'
  | 'Law, Gov’t & Politics'
  | 'Mature'
  | 'News'
  | 'Outdoor & Hunting'
  | 'Pets'
  | 'Science'
  | 'Shopping'
  | 'Sports'
  | 'Style & Fashion'
  | 'Technology & Computing'
  | 'Travel'
  | 'General Interest';

export interface AdCampaignInfo {
  podcastId?: string;
  remoteEpisodeId?: string;
  youtubeVideoUrl?: string;
  campaignBudgetMilliCent?: number;
  sponsorPlacement?: SponsorPlacement;
  campaignDuration?: string;
  targetCategories: CampaignCategories[] | null;
  targetCountryCodes: string[] | null;
  isTargetingContactRequest: boolean;
  // The following categories have been removed from the UI but will still be returned by the api
  targetGenderGroups: string[] | null;
  targetDeviceTypes: string[] | null;
  targetAgeGroups: string[] | null;
}

export interface GoogleAttribution {
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  clientId?: string;
  sessionId?: number;
}

export interface CheckoutAttribution {
  google?: GoogleAttribution;
}

export interface CreateAdCampaignArgs
  extends Omit<
    AdCampaignInfo,
    'targetGenderGroups' | 'targetDeviceTypes' | 'targetAgeGroups'
  > {
  checkoutAttribution?: CheckoutAttribution;
}

export interface CreateAdCampaignResponse {
  requestId: number;
  checkoutUrl: string;
  isCheckoutRequired: boolean;
}

export interface AdCampaignPodcastContentDetail {
  podcastTitle: string;
  podcastThumbnailUrl: string;
  episodeTitle: string;
  episodeThumbnailUrl: string | null;
}

export interface AdCampaignYoutubeContentDetail {
  youtubeVideoUrl: string;
  videoId: string;
  title: string;
  thumbnailUrl: string | null;
}

export interface AdCampaignResult
  extends Omit<AdCampaignInfo, 'isTargetingContactRequest'> {
  contentType: SponsorContentType;
  contentDetail:
    | AdCampaignPodcastContentDetail
    | AdCampaignYoutubeContentDetail
    | null;
  createdAt: number;
  // campaigns created before this property was added will return null
  isTargetingContactRequest: boolean | null;
}

export interface PodcastAdCampaignResult extends AdCampaignResult {
  contentType: 'podcastEpisode';
  contentDetail: AdCampaignPodcastContentDetail;
}

export interface YoutubeAdCampaignResult extends AdCampaignResult {
  contentType: 'youtubeVideo';
  contentDetail: AdCampaignYoutubeContentDetail;
}

export type AdCampaignResultWithContentDetail =
  | PodcastAdCampaignResult
  | YoutubeAdCampaignResult;

export interface GetAdCampaignResponse {
  requests: AdCampaignResult[];
}

export interface GetSelfServeSponsorRequestArgs {
  selfServeSponsorRequestId: number;
}

export type GetSelfServeSponsorResponse = AdCampaignResult;

export enum RoleName {
  advertiser = 'advertiser',
  publisher = 'publisher',
}

export type Role = {
  name: RoleName;
};

export type UserRoles = {
  roles: Role[];
};

export interface GetPodcastMetricsArgs {
  podcastId: string;
}

export interface GetPodcastMetricsResponse {
  title: string;
  playCount: number;
  followCount: number;
  revenueShareMilliCent?: number;
}

export interface GetPodcastMetricsByEpisodeArgs {
  podcastId: string;
}

export interface EpisodeMetrics {
  title: string;
  thumbnailUrl: string;
  publishedAtMillis: number;
  playCount: number;
  remoteEpisodeId: string;
  durationMillis: number;
}
export interface GetPodcastMetricsByEpisodeResponse {
  episodes: EpisodeMetrics[];
  nextEpisodePubAtMillis: number;
}

export interface GetPodcastMetricsByEpisodeOpts {
  nextEpisodePubAtMillis?: number;
}

export interface GetPodcastMetricsByDayArgs {
  podcastId: string;
  startMillis: number;
  endMillis?: number;
  remoteEpisodeId?: string;
}

export interface PodcastMetricsByDay {
  dayMillis: number;
  playCount: number;
}

export interface GetPodcastMetricsByDayResponse {
  data: PodcastMetricsByDay[];
}
